/**
 * Axis auto-suggest — suggests the best adjacency axis for a node
 * based on keyword matching against its name and description.
 *
 * Returns up to 3 suggestions ranked by confidence score (0–100).
 */

import type { AdjacencyAxis } from '../types/adjacency';

export interface AxisSuggestion {
  axis: AdjacencyAxis;
  confidence: number; // 0–100
}

/**
 * Keyword dictionaries per axis. Each entry is [keyword, weight].
 * Weight 2 = strong signal, 1 = moderate signal.
 * Keywords are matched case-insensitively against name + description.
 */
const AXIS_KEYWORDS: Record<Exclude<AdjacencyAxis, 'Core'>, [string, number][]> = {
  'New Technologies & Capabilities': [
    ['sensor', 2], ['sensor fusion', 2], ['DAS', 2], ['wireless', 2],
    ['LTE', 2], ['pLTE', 2], ['5G', 2], ['IoT', 1], ['digital', 1],
    ['AI', 1], ['analytics', 1], ['RF', 2], ['digital signal', 2],
    ['innovation', 2], ['form factor', 2], ['integration', 1],
    ['service center', 1], ['field support', 1], ['factory support', 1],
    ['hardware', 1], ['systems', 1], ['safety', 1],
  ],
  'New Geographies': [
    ['international', 2], ['australia', 2], ['africa', 2], ['south africa', 2],
    ['india', 1], ['global', 1], ['export', 1], ['turkey', 2], ['canada', 2],
    ['central america', 2], ['distribution', 1], ['east coal', 1],
    ['central coal', 1], ['west coal', 1], ['MNM', 1],
    ['reseller', 1], ['VAR', 1], ['manufacturing', 1],
  ],
  'New Markets & Customers': [
    ['hard rock', 2], ['reclamation', 2], ['plant', 1], ['plants', 1],
    ['DoD', 2], ['federal', 2], ['civilian', 2], ['maritime', 1],
    ['smart building', 2], ['smart city', 2], ['smart cities', 2],
    ['AgTech', 2], ['agriculture', 1], ['ag asset', 1],
    ['shipping port', 2], ['mine services', 1], ['headlamp', 1],
    ['mesh node', 1], ['WiFi', 1], ['managed services', 1],
    ['predictive maintenance', 1], ['vehicle control', 1],
    ['proximity', 1], ['gold', 1], ['copper', 1], ['potash', 1],
  ],
  'New Products': [
    ['tag', 1], ['reader', 1], ['device', 1], ['equipment', 1],
    ['proximity detection', 2], ['asset tracking', 2], ['UG proximity', 2],
    ['sentinel', 1], ['serverlink', 2], ['analytics', 1],
    ['branded platform', 2], ['digital transformation', 2],
    ['predictive compliance', 2], ['belt control', 2], ['monitoring', 1],
    ['dispatch', 2], ['location mapping', 2], ['API', 1],
    ['gamification', 2], ['virtual assistant', 2], ['advanced analytics', 1],
    ['channel', 1], ['multitenancy', 1], ['stockpile', 2],
    ['void detection', 2], ['core enhancement', 2],
    ['IR&D', 2], ['FR&D', 2], ['SES', 1],
    ['mine integrator', 2], ['navy', 1],
  ],
  'New Services': [
    ['service', 1], ['consulting', 2], ['training', 1], ['support', 1],
    ['SaaS', 2], ['subscription', 2], ['as a service', 2],
    ['staffing', 2], ['augmentation', 2], ['fleet management', 2],
    ['autonomous vehicle', 2], ['teleremote', 2], ['semi-autonomous', 2],
    ['telematics', 2], ['vehicle telematics', 2],
    ['queuing', 2], ['route optimization', 2],
    ['predictive', 1], ['prescriptive', 1],
    ['surface coal', 1], ['potash', 1], ['aggregates', 1],
    ['metals', 1], ['non-metals', 1],
  ],
  'Forward Integration': [
    ['install', 1], ['deploy', 1], ['maintain', 1], ['operate', 1],
    ['manage', 1], ['mine rescue', 2], ['supply chain', 2],
    ['system integrator', 2], ['wireless integrator', 2],
    ['acquisition', 2], ['OEM disrupter', 2], ['OEM', 1],
    ['in-building', 2], ['mining software', 2],
  ],
  'Backward Integration': [
    ['manufacture', 2], ['manufacturing', 2], ['supply chain', 1],
    ['raw material', 2], ['component', 1], ['enclosure', 2],
    ['mechanical', 1], ['board', 1], ['waveboard', 1], ['WaveLink', 2],
    ['multi-band', 2], ['infrastructure', 2],
    ['wireless standard', 2], ['no code', 2], ['mobile app', 2],
    ['AI insights', 2], ['next gen', 1], ['sentinel', 1],
  ],
  'New Channels': [
    ['OEM', 1], ['partner', 1], ['reseller', 1], ['distributor', 1],
    ['channel', 1], ['direct sales', 2], ['certification', 2],
    ['marketing', 2], ['production', 1], ['engineering', 1],
    ['finance', 1], ['sales', 1], ['NBD', 2], ['cabling', 2],
    ['pLTE partner', 2], ['PS DAS', 2], ['cellular DAS', 2],
    ['embedded software', 2], ['software', 1], ['platform dev', 2],
    ['UX design', 2], ['aesthetics', 1], ['ML/AI', 2],
    ['account team', 2], ['vehicle OEM', 2], ['strategic partner', 2],
  ],
};

/**
 * Suggest axes for a node based on its name and description text.
 * Returns up to 3 suggestions sorted by confidence (highest first).
 * Only includes suggestions with confidence > 0.
 */
export function suggestAxes(name: string, description: string): AxisSuggestion[] {
  const text = `${name} ${description}`.toLowerCase();
  if (text.trim().length < 2) return [];

  const scores: { axis: Exclude<AdjacencyAxis, 'Core'>; raw: number }[] = [];

  for (const [axis, keywords] of Object.entries(AXIS_KEYWORDS) as [Exclude<AdjacencyAxis, 'Core'>, [string, number][]][]) {
    let raw = 0;

    for (const [keyword, weight] of keywords) {
      const kw = keyword.toLowerCase();
      // Check for whole-word-ish match (word boundary or start/end of string)
      const idx = text.indexOf(kw);
      if (idx === -1) continue;

      // Verify it's not a substring of a larger word (simple boundary check)
      const before = idx > 0 ? text[idx - 1] : ' ';
      const after = idx + kw.length < text.length ? text[idx + kw.length] : ' ';
      const boundaryChars = /[\s,.\-/:;()"'&]/;
      const atBoundary = boundaryChars.test(before) && boundaryChars.test(after);

      if (atBoundary) {
        raw += weight;
      } else if (kw.length >= 4) {
        // For longer keywords, still count partial matches at reduced weight
        raw += weight * 0.5;
      }
    }

    if (raw > 0) scores.push({ axis, raw });
  }

  if (scores.length === 0) return [];

  // Normalize to 0–100 confidence. Max possible raw is roughly 6–10 for a typical match.
  const maxRaw = Math.max(...scores.map(s => s.raw));
  const cap = Math.max(maxRaw, 4); // Minimum cap to avoid inflating weak signals

  return scores
    .sort((a, b) => b.raw - a.raw)
    .slice(0, 3)
    .map(s => ({
      axis: s.axis,
      confidence: Math.min(100, Math.round((s.raw / cap) * 100)),
    }));
}
